import { AlreadyRead } from './entity/valueObjects/alreadyRead';

import type { PageEntity } from './entity/pageEntity';
import type { PageIRepository } from './entity/pageIRepository';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';

import { DomainServiceBase } from '@/domain/domainServiceBase';
import { generateWebpThumbnail, loadAsWebp } from '@/utils/image';

/**
 * 連絡帳ドメインサービス
 */
export class ChildNotebookDomainService extends DomainServiceBase {
  private pageRepository!: PageIRepository;

  private wagakoNurseryApi!: WagakoNurseryIApi;

  /**
   * Injection
   * @param pageRepository pageRepository
   * @param wagakoNurseryApi wagakoNurseryApi
   */
  public inject(
    pageRepository: PageIRepository,
    wagakoNurseryApi: WagakoNurseryIApi,
  ) {
    this.pageRepository = pageRepository;
    this.wagakoNurseryApi = wagakoNurseryApi;
  }

  /**
   * ページリスト取得
   * @param date トークン
   * @returns PageEntity[]
   */
  async getPageList(date: string) {
    const result = await this.pageRepository.getAllListWithKey(date);
    return result;
  }

  /**
   * ページ作成
   * @param entity 入力
   * @returns 結果
   */
  async createPage(
    entity: PageEntity,
  ) {
    const result = await this.pageRepository.create(entity);
    return result;
  }

  /**
   * ページ更新
   * @param entity 入力
   * @returns 結果
   */
  async updatePage(
    entity: PageEntity,
  ) {
    const result = await this.pageRepository.update(entity);
    return result;
  }

  /**
   * ページ削除
   * @param entity 入力
   * @returns 結果
   */
  async deletePage(
    entity: PageEntity,
  ) {
    const result = await this.pageRepository.delete(entity);
    return result;
  }

  /**
   * sendNurseryPage
   * @param entity entity
   */
  async sendNurseryPage(
    entity: PageEntity,
  ) {
    await this.wagakoNurseryApi.send(entity);
  }

  /**
   * removeNurseryPage
   * @param entity entity
   */
  async removeNurseryPage(
    entity: PageEntity,
  ) {
    await this.wagakoNurseryApi.remove(entity);
  }

  /**
   * 既読
   * @param entity Entity
   */
  async alreadyRead(
    entity: PageEntity,
  ) {
    entity.setAlreadyRead(AlreadyRead.create(true));
    await this.wagakoNurseryApi.reply(entity);
  }

  /**
   * ファイルの保存処理
   * @param files File.
   * @param entity entity
   */
  async saveAttachments(
    files: File[],
    entity: PageEntity,
  ) {
    const promises: Promise<boolean>[] = [];
    files.forEach((v) => {
      promises.push(this.saveAttachment(v, entity));
    });
    const result = await Promise.all(promises);
    return result;
  }

  /**
   * ファイルの保存処理
   * @param file File.
   * @param entity Entity.
   * @returns 結果
   */
  private async saveAttachment(
    file: File,
    entity: PageEntity,
  ) {
    try {
      await this.wagakoNurseryApi.saveFileToS3(
        `${entity.s3FolderPath}${file.name}`,
        entity.pub.replyId,
        file,
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 写真の保存処理
   * @param files File.
   * @param entity entity
   */
  async saveImages(
    files: File[],
    entity: PageEntity,
  ) {
    const promises: Promise<boolean>[] = [];
    files.forEach((v) => {
      promises.push(this.saveImage(v, entity));
    });
    const result = await Promise.all(promises);
    return result;
  }

  /**
   * 写真の保存処理
   * ①オリジナル画像保存
   * ②サムネイル保存
   * @param file File.
   * @param entity Entity.
   * @returns 結果
   */
  private async saveImage(
    file: File,
    entity: PageEntity,
  ) {
    try {
      // 元画像保存
      const webpBlob = await loadAsWebp(file);
      // 保存時のファイル名をここで決定
      const name = `${file.name.split('.').slice(0, -1).join('.')}.webp`;
      await this.wagakoNurseryApi.saveFileToS3(
        `${entity.s3FolderPath}${name}`,
        entity.pub.replyId,
        webpBlob,
      );
      // サムネイル保存
      const thumbnailBlob = await generateWebpThumbnail(webpBlob);
      await this.wagakoNurseryApi.saveFileToS3(
        `${entity.s3FolderPath}${name}_thumbnail.webp`,
        entity.pub.replyId,
        thumbnailBlob,
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * ファイルの削除処理
   * @param entity entity
   */
  async removeFiles(
    entity: PageEntity,
  ) {
    const promises: Promise<{
      /** メッセージ */
      message: string,
      /** データ */
      data?: ArrayBuffer,
    }>[] = [];
    entity.attachments.forEach((v) => {
      promises.push(this.wagakoNurseryApi.deleteFileFromS3(
        `${entity.s3FolderPath}${v}`,
        entity.pub.replyId,
      ));
    });
    entity.images.forEach((v) => {
      promises.push(this.wagakoNurseryApi.deleteFileFromS3(
        `${entity.s3FolderPath}${v}`,
        entity.pub.replyId,
      ));
      promises.push(this.wagakoNurseryApi.deleteFileFromS3(
        `${entity.s3FolderPath}${v}_thumbnail.webp`,
        entity.pub.replyId,
      ));
    });
    await Promise.all(promises);
  }
}
